import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { 
  Check, ArrowRight, Zap, Building2, Crown, BedDouble, Clock, AlertTriangle,
  FileText, RefreshCw, Shield, BarChart3, Users, Download, Play, Star,
  CheckCircle2, XCircle, Timer, TrendingUp, Sparkles, ChevronRight,
  Calendar, AlertCircle, Phone, Mail, MessageCircle
} from 'lucide-react';

const EInvoiceProductPage = () => {
  const navigate = useNavigate();
  const [daysUntilDeadline, setDaysUntilDeadline] = useState(0);
  const [showUrgencyBanner, setShowUrgencyBanner] = useState(true);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Calculate days until July 2025 deadline (Phase 3)
    const deadline = new Date('2025-07-01');
    const today = new Date();
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setDaysUntilDeadline(diffDays > 0 ? diffDays : 0);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Shared Navigation */}
      <LandingNavBar />
      
      {/* Urgency Banner - positioned below the nav */}
      {showUrgencyBanner && (
        <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white py-3 px-4 relative mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm md:text-base">
            <AlertTriangle className="h-5 w-5 animate-pulse" />
            <span className="font-medium">
              ⏰ LHDN Phase 3 Deadline: <strong>July 2025</strong> — Only <strong>{daysUntilDeadline} days left</strong> for hotels with revenue &gt; RM500K
            </span>
            <Button 
              size="sm" 
              variant="secondary" 
              className="ml-2 bg-white text-red-600 hover:bg-red-50"
              onClick={() => scrollToSection('pricing')}
            >
              Get Compliant Now
            </Button>
            <button 
              onClick={() => setShowUrgencyBanner(false)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Hero Section - add top padding when urgency banner is hidden */}
      <section className={`relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-white py-16 md:py-24 ${!showUrgencyBanner ? 'mt-16 lg:mt-20' : ''}`}>
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.5))]" />
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Copy */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full text-blue-700 text-sm font-medium">
                <Shield className="h-4 w-4" />
                LHDN MyInvois Certified Solution
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                StayMii E-Invoice Compliance
                <span className="block text-blue-600">Made Automatic</span>
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed">
                The middleware that connects your <strong>PMS</strong> to <strong>LHDN MyInvois</strong>. 
                Auto-generate compliant e-Invoices from bookings, submit in one click, and stay 100% compliant 
                with Malaysia's e-Invoice mandate.
              </p>

              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Official Beds24 Integration
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  99% First-Time Approval Rate
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Built for Malaysia
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-6"
                  onClick={() => navigate('/einvoice/portal/auth')}
                >
                  Start Your Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="text-lg px-8 py-6"
                  onClick={() => scrollToSection('demo')}
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch Demo
                </Button>
              </div>

              <p className="text-sm text-slate-500">
                ✓ 14-day free trial &nbsp;•&nbsp; ✓ No credit card required &nbsp;•&nbsp; ✓ Setup in under 5 minutes
              </p>
            </div>

            {/* Right: Product Screenshot */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur-2xl opacity-20" />
              <div className="relative bg-white rounded-2xl shadow-2xl border overflow-hidden">
                <div className="bg-slate-100 px-4 py-3 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <span className="text-xs text-slate-500 ml-2">StayMii Dashboard</span>
                </div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_17c5bed0-a4c0-4c10-a567-0dc5d17ff94d/artifacts/3jin69o6_5DC3E673-8302-4001-B2F3-BC14E59F21A8.png" 
                  alt="StayMii Dashboard"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-slate-900 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-white/80 text-sm">
            <span>Used and Recommended by Hotelier and Property Managements Operator</span>
            <div className="flex items-center gap-8">
              <span className="font-semibold text-white">Heritage Hotel</span>
              <span className="font-semibold text-white">Serviced Suites</span>
              <span className="font-semibold text-white">Beachfront Resorts</span>
              <span className="font-semibold text-white">Homestay</span>
              <span className="font-semibold text-white">Boutique Hotels</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - Redesigned */}
      <section className="py-20 bg-gradient-to-b from-blue-50/50 to-white" id="problem">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="mb-6 bg-red-100 text-red-600 border-0 px-4 py-1.5 text-sm font-semibold">
              THE PROBLEM
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Your PMS Can't Handle LHDN e-Invoicing
            </h2>
            <p className="text-xl md:text-2xl font-semibold text-red-500 mb-6">
              Manual Workarounds Are Costing You Time & Money
            </p>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Malaysian hospitality businesses face a growing compliance gap.
              <br className="hidden md:block" />
              Your PMS manages bookings — but it doesn't generate LHDN-compliant e-Invoices.
            </p>
          </div>

          {/* What's Happening Right Now */}
          <h3 className="text-xl md:text-2xl font-bold text-slate-900 text-center mb-8">
            What's Happening Right Now
          </h3>

          {/* Problem Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Card 1: No Native e-Invoice */}
            <Card className="bg-white shadow-lg border-0 rounded-2xl overflow-hidden">
              <CardHeader className="pb-0">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                    <XCircle className="h-5 w-5 text-red-500" />
                  </div>
                  <CardTitle className="text-lg font-bold text-slate-900">No Native e-Invoice in PMS</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                {/* Illustration */}
                <div className="flex justify-center mb-6 py-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_17c5bed0-a4c0-4c10-a567-0dc5d17ff94d/artifacts/gj3apf8h_Untitled_design_-_1.jpeg"
                    alt="PMS with broken chain link"
                    className="h-52 md:h-60 w-auto object-contain"
                  />
                </div>
                <p className="text-slate-600 text-sm mb-4">
                  Beds24, Little Hotelier — none support LHDN e-Invoicing. 
                  You're forced into disconnected, manual workflows.
                </p>
                <div className="flex items-center gap-2 text-red-500 font-medium text-sm">
                  <Clock className="h-4 w-4" />
                  2-4 hours wasted daily
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Painful Manual Workflow */}
            <Card className="bg-white shadow-lg border-0 rounded-2xl overflow-hidden">
              <CardHeader className="pb-0">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-blue-500" />
                  </div>
                  <CardTitle className="text-lg font-bold text-slate-900">Painful Manual Workflow</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                {/* Illustration - Flow diagram */}
                <div className="flex justify-center mb-6 py-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_17c5bed0-a4c0-4c10-a567-0dc5d17ff94d/artifacts/qk0nwal4_839698e0-4516-4345-9744-82479fcd9ebe.jpeg"
                    alt="Manual workflow: Export to Excel to Re-enter"
                    className="h-52 md:h-60 w-auto object-contain"
                  />
                </div>
                <p className="text-slate-600 text-sm mb-4">
                  Export → Excel → Re-enter → Accounting → Submit to LHDN. Every. Single. Invoice.
                </p>
                <div className="flex items-center gap-2 text-slate-500 font-medium text-sm">
                  <RefreshCw className="h-4 w-4" />
                  5-6 systems involved
                </div>
              </CardContent>
            </Card>

            {/* Card 3: Compliance Risk */}
            <Card className="bg-white shadow-lg border-0 rounded-2xl overflow-hidden">
              <CardHeader className="pb-0">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                  </div>
                  <CardTitle className="text-lg font-bold text-slate-900">Compliance Risk (2026)</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                {/* Illustration - Fine warning */}
                <div className="flex items-center justify-center mb-6 py-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_17c5bed0-a4c0-4c10-a567-0dc5d17ff94d/artifacts/s8kbp7p0_a43db460-bf41-4e86-9d7c-e55dfd17c03a.jpeg"
                    alt="Compliance fine warning document"
                    className="h-52 md:h-60 w-auto object-contain"
                  />
                </div>
                <p className="text-slate-600 text-sm mb-4">
                  By Jan 2026, e-Invoicing is mandatory. Manual processes = errors, rejections, penalties.
                </p>
                <div className="flex items-center gap-2 text-red-500 font-medium text-sm">
                  <AlertCircle className="h-4 w-4" />
                  Up to RM200,000 fines
                </div>
              </CardContent>
            </Card>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-full"
              onClick={() => navigate('/einvoice/portal/auth')}
            >
              We Can Automate This
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Deadline Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-red-500">THE LHDN E-INVOICE MANDATE</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The Deadline is Non-Negotiable
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              LHDN is rolling out mandatory e-Invoicing in phases. Your deadline depends on your revenue.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {/* Phase 1 */}
            <div className="bg-slate-800 rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/10 rounded-full -mr-10 -mt-10" />
              <Badge className="bg-green-500/20 text-green-400 mb-4">Completed</Badge>
              <h3 className="text-2xl font-bold mb-2">Phase 1</h3>
              <p className="text-slate-400 text-sm mb-4">August 2024</p>
              <p className="text-slate-300">Revenue &gt; RM100M</p>
              <CheckCircle2 className="absolute bottom-4 right-4 h-8 w-8 text-green-500" />
            </div>

            {/* Phase 2 */}
            <div className="bg-slate-800 rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/10 rounded-full -mr-10 -mt-10" />
              <Badge className="bg-green-500/20 text-green-400 mb-4">Completed</Badge>
              <h3 className="text-2xl font-bold mb-2">Phase 2</h3>
              <p className="text-slate-400 text-sm mb-4">January 2025</p>
              <p className="text-slate-300">Revenue &gt; RM25M</p>
              <CheckCircle2 className="absolute bottom-4 right-4 h-8 w-8 text-green-500" />
            </div>

            {/* Phase 3 - URGENT */}
            <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-xl p-6 relative overflow-hidden ring-4 ring-orange-400/50">
              <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10" />
              <Badge className="bg-white/20 text-white mb-4 animate-pulse">Coming Soon</Badge>
              <h3 className="text-2xl font-bold mb-2">Phase 3</h3>
              <p className="text-white/80 text-sm mb-4">July 2025</p>
              <p className="text-white">Revenue &gt; RM500K</p>
              <div className="mt-4 bg-white/20 rounded-lg px-3 py-2 text-sm font-bold">
                {daysUntilDeadline} days left
              </div>
            </div>

            {/* Phase 4 */}
            <div className="bg-slate-800 rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full -mr-10 -mt-10" />
              <Badge className="bg-blue-500/20 text-blue-400 mb-4">Final Phase</Badge>
              <h3 className="text-2xl font-bold mb-2">Phase 4</h3>
              <p className="text-slate-400 text-sm mb-4">January 2026</p>
              <p className="text-slate-300">ALL Businesses</p>
              <Calendar className="absolute bottom-4 right-4 h-8 w-8 text-blue-400" />
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-red-500/20 text-red-300 px-6 py-3 rounded-full">
              <AlertCircle className="h-5 w-5" />
              <span>
                By January 2026, every hotel, homestay, and service apartment must issue LHDN-compliant e-Invoices 
                or face penalties up to <strong>RM200,000</strong>. This is not optional.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white" id="how-it-works">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 text-blue-600 border-blue-200 bg-blue-50">THE SOLUTION</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              From Booking to LHDN in<br/>
              <span className="text-blue-600">4 Simple Steps</span>
            </h2>
            <p className="text-lg text-slate-600">
              Connect once. Automate forever.
            </p>
          </div>

          {/* Animated GIF Preview */}
          <div className="mb-16 flex justify-center">
            <img 
              src="/staymii_4steps.gif"
              alt="StayMii 4 Steps Process Animation"
              className="rounded-2xl shadow-2xl max-w-3xl w-full"
            />
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-6 shadow-lg border relative z-10">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 text-blue-600 font-bold text-xl">
                  1
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Connect Your PMS</h3>
                <p className="text-slate-600">
                  Connect your PMS account in minutes. Bookings sync automatically in real time — no manual input required.
                </p>
                <p className="text-sm text-blue-600 font-medium mt-2">
                  No exports. No spreadsheets. No errors.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-blue-200" />
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-6 shadow-lg border relative z-10">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 text-blue-600 font-bold text-xl">
                  2
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Auto-Sync Bookings</h3>
                <p className="text-slate-600">
                  Bookings sync automatically. Guest details, charges, and taxes — all captured.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-blue-200" />
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-6 shadow-lg border relative z-10">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 text-blue-600 font-bold text-xl">
                  3
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Review & Customize</h3>
                <p className="text-slate-600">
                  Review generated invoices, edit customer details if needed, add missing TIN.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-blue-200" />
            </div>

            {/* Step 4 */}
            <div>
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 shadow-lg text-white">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4 font-bold text-xl">
                  4
                </div>
                <h3 className="text-xl font-bold mb-2">Submit to LHDN</h3>
                <p className="text-blue-100">
                  One click sends to MyInvois. Get validated PDF with official QR code instantly.
                </p>
                <CheckCircle2 className="h-8 w-8 mt-4 text-green-400" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Automate Your Entire E-Invoice Workflow */}
      <section className="py-20 bg-gradient-to-b from-blue-50/80 to-blue-100/50" id="features">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12 relative">
            <Badge className="mb-4 bg-white text-blue-600 border-0 shadow-sm px-4 py-1.5">
              <Zap className="h-4 w-4 mr-1 inline" />
              POWERFUL FEATURES FOR HOTELS
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Automate Your Entire E-Invoice Workflow
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              From booking to LHDN submission—everything runs automatically, accurately, and at scale.
            </p>
          </div>

          {/* Dashboard Preview Image */}
          <div className="mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_17c5bed0-a4c0-4c10-a567-0dc5d17ff94d/artifacts/3jin69o6_5DC3E673-8302-4001-B2F3-BC14E59F21A8.png"
              alt="StayMii Dashboard - E-Invoice Management System"
              className="w-full max-w-6xl mx-auto rounded-2xl shadow-2xl"
            />
          </div>

          {/* Features Grid - Bento Style */}
          <div className="grid lg:grid-cols-12 gap-4">
            {/* Left Column - 3 Vertical Cards */}
            <div className="lg:col-span-4 space-y-4">
              {/* Card 1: Official PMS Integration */}
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <RefreshCw className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Official PMS Integration</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Connect your PMS account in minutes. Bookings sync automatically in real time — no manual input required.
                </p>
                <Badge className="bg-blue-50 text-blue-700 border-0 text-xs">
                  No exports. No spreadsheets. No errors.
                </Badge>
              </div>

              {/* Card 2: Bulk Submission */}
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <FileText className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Bulk Submission (50+ Invoices)</h3>
                <p className="text-slate-600 text-sm">
                  Submit hundreds of invoices in seconds. Built for month-end rush.
                </p>
              </div>

              {/* Card 3: Guest TIN Management */}
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <Users className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Guest TIN Management</h3>
                <p className="text-slate-600 text-sm">
                  Save guest details once. Reuse forever. Faster repeat checkouts.
                </p>
              </div>
            </div>

            {/* Right Side - 2x3 Grid */}
            <div className="lg:col-span-8 grid md:grid-cols-2 gap-4">
              {/* Row 1 */}
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Auto E-Invoice Generation</h3>
                <p className="text-slate-600 text-sm">
                  Every booking becomes a compliant invoice instantly— individual or consolidated.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Smart Validation Engine</h3>
                <p className="text-slate-600 text-sm">
                  Catch errors before submission. Achieve up to 99% first-time approval rate.
                </p>
              </div>

              {/* Row 2 */}
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Bulk Submission (50+ Invoices)</h3>
                <p className="text-slate-600 text-sm">
                  Submit hundreds of invoices in seconds. Built for month-end rush.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">PDF + LHDN QR Code</h3>
                <p className="text-slate-600 text-sm">
                  Auto-generate compliant PDFs with official QR validation.
                </p>
              </div>

              {/* Row 3 */}
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                  <Building2 className="h-6 w-6 text-indigo-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Multi-Property Dashboard</h3>
                <p className="text-slate-600 text-sm">
                  Control all your hotels from one place. Owner-level visibility.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-cyan-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Revenue & Compliance Analytics</h3>
                <p className="text-slate-600 text-sm">
                  Track submissions, revenue trends, and compliance status in real time.
                </p>
              </div>

              {/* CTA Row */}
              <div className="md:col-span-2 bg-white rounded-2xl p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Ready to stop manual invoicing?</h3>
                  <p className="text-slate-500 text-sm">Not only digital. Sentgented cost.</p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                    onClick={() => navigate('/einvoice/portal/auth')}
                  >
                    Start Free Trial
                  </Button>
                  <Button 
                    variant="outline" 
                    className="px-6"
                    onClick={() => navigate('/contact')}
                  >
                    Book Demo
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-slate-50" id="pricing">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header with Launch Badge */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full text-white text-sm font-bold mb-4 animate-pulse">
              🔥 Founding Member Pricing — Only 50 Hotels Eligible
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Choose Your Plan
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-2">
              Official PMS-integrated e-Invoice solution for Malaysian hotels
            </p>
            <p className="text-base text-green-600 font-semibold">
              🔒 Lock this price forever. Future price increases won't affect you.
            </p>
          </div>

          {/* ROI Framing */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-blue-600 font-medium text-sm">💰 Costs less than 1 room booking/month</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg border border-green-200">
              <span className="text-green-600 font-medium text-sm">⏱️ Save 10+ hours/month on manual e-Invoice work</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Starter */}
            <Card className="relative hover:shadow-xl transition-shadow">
              <div className="absolute -top-3 left-4">
                <Badge className="bg-orange-500 text-white text-[10px]">FOUNDING PRICE</Badge>
              </div>
              <CardHeader className="pt-6">
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                  <BedDouble className="h-6 w-6 text-slate-600" />
                </div>
                <CardTitle>Starter</CardTitle>
                <CardDescription>For small homestays getting started</CardDescription>
                <div className="mt-4">
                  <span className="text-lg text-slate-400 line-through mr-2">RM 99</span>
                  <span className="text-4xl font-bold text-slate-900">RM 59</span>
                  <span className="text-slate-500">/month</span>
                </div>
                <Badge variant="outline" className="mt-2">
                  <BedDouble className="h-3 w-3 mr-1" />
                  Up to 10 rooms
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                {["Up to 2 properties", "Seamless PMS integration", "Auto e-Invoice generation", "Manual submission mode", "PDF + QR code output", "Email support (72hr)"].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-slate-600">{f}</span>
                  </div>
                ))}
              </CardContent>
              <CardFooter>
                <Button className="w-full" variant="outline" onClick={() => navigate('/einvoice/portal/auth')}>
                  Start Free Trial
                </Button>
              </CardFooter>
            </Card>

            {/* Growth - POPULAR */}
            <Card className="relative border-blue-400 ring-2 ring-blue-400 hover:shadow-xl transition-shadow lg:scale-105">
              <div className="absolute -top-3 left-4">
                <Badge className="bg-orange-500 text-white text-[10px]">FOUNDING PRICE</Badge>
              </div>
              <div className="absolute -top-3 right-4">
                <Badge className="bg-blue-600">⭐ Best for Growing Hotels</Badge>
              </div>
              <CardHeader className="pt-6">
                <p className="text-xs text-blue-600 font-medium mb-2">Recommended for 80% of properties</p>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle>Growth</CardTitle>
                <CardDescription>Best for Growing Hotels</CardDescription>
                <div className="mt-4">
                  <span className="text-lg text-slate-400 line-through mr-2">RM 199</span>
                  <span className="text-4xl font-bold text-slate-900">RM 119</span>
                  <span className="text-slate-500">/month</span>
                </div>
                <Badge variant="outline" className="mt-2 bg-blue-50">
                  <BedDouble className="h-3 w-3 mr-1" />
                  Up to 30 rooms
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                {["Up to 5 properties", "Everything in Starter, plus:", "Auto-submit to LHDN", "Individual + Consolidated invoices", "Bulk submission (50+ at once)", "Smart pre-validation", "Customer TIN management", "Email + Chat support (24hr)"].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-slate-600">{f}</span>
                  </div>
                ))}
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/einvoice/portal/auth')}>
                  Start Free Trial
                </Button>
              </CardFooter>
            </Card>

            {/* Pro */}
            <Card className="relative hover:shadow-xl transition-shadow">
              <div className="absolute -top-3 left-4">
                <Badge className="bg-orange-500 text-white text-[10px]">FOUNDING PRICE</Badge>
              </div>
              <CardHeader className="pt-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Building2 className="h-6 w-6 text-purple-600" />
                </div>
                <CardTitle>Pro</CardTitle>
                <CardDescription>Multi-property control and insights</CardDescription>
                <div className="mt-4">
                  <span className="text-lg text-slate-400 line-through mr-2">RM 299</span>
                  <span className="text-4xl font-bold text-slate-900">RM 199</span>
                  <span className="text-slate-500">/month</span>
                </div>
                <Badge variant="outline" className="mt-2">
                  <BedDouble className="h-3 w-3 mr-1" />
                  Up to 50 rooms
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                {["Up to 10 properties", "Up to 3 PMS accounts", "Everything in Growth, plus:", "Multi-property dashboard", "Role-based access", "Advanced analytics & reports", "Audit trail logs", "Export reports (Excel/CSV)", "Priority processing", "Phone support"].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-slate-600">{f}</span>
                  </div>
                ))}
              </CardContent>
              <CardFooter>
                <Button className="w-full" variant="outline" onClick={() => navigate('/einvoice/portal/auth')}>
                  Start Free Trial
                </Button>
              </CardFooter>
            </Card>

            {/* Enterprise */}
            <Card className="relative bg-gradient-to-br from-slate-900 to-slate-800 text-white hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Crown className="h-6 w-6 text-amber-400" />
                </div>
                <CardTitle className="text-white">Enterprise</CardTitle>
                <CardDescription className="text-slate-300">For hotel chains and partners</CardDescription>
                <div className="mt-4">
                  <span className="text-3xl font-bold">Custom Pricing</span>
                </div>
                <Badge variant="outline" className="mt-2 border-amber-400/50 text-amber-400">
                  Unlimited rooms
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                {["Unlimited properties", "Unlimited PMS accounts", "Everything in Pro, plus:", "API access", "Custom integrations", "White-label option", "Dedicated account manager", "SLA guarantee (99.9%)", "Custom training"].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-amber-400" />
                    <span className="text-slate-300">{f}</span>
                  </div>
                ))}
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={() => navigate('/contact')}>
                  Contact Sales
                </Button>
              </CardFooter>
            </Card>
          </div>

          {/* Launch Bonuses Section */}
          <div className="mt-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">🎁 Launch Bonuses Included</h3>
              <p className="text-slate-600">Total value: <span className="font-bold text-green-600">RM1,000+</span> — Included FREE with any plan</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: '🎁', text: 'Free onboarding (worth RM299)' },
                { icon: '🎁', text: 'Free PMS integration setup' },
                { icon: '🎁', text: 'Free ebook: E-Invoice Guide for Malaysian Hotels' },
                { icon: '🎁', text: 'Priority support' },
              ].map((bonus, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-white rounded-xl p-4 border border-blue-100">
                  <span className="text-2xl">{bonus.icon}</span>
                  <span className="text-sm font-medium text-slate-700">{bonus.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Us Button */}
          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">Have questions? We're here to help you get started.</p>
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              onClick={() => navigate('/contact')}
            >
              Contact Us Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Team/Support Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-700 border-0">
                DEDICATED SUPPORT TEAM
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Real People. Real Support.<br/>
                <span className="text-blue-600">Always Here to Help.</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Our team of e-Invoice specialists is ready to help you every step of the way. 
                From onboarding to ongoing support, we're committed to your success.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span className="text-slate-700">Free onboarding & setup assistance</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span className="text-slate-700">Dedicated account manager for Pro & Enterprise</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span className="text-slate-700">Email, chat & phone support available</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span className="text-slate-700">Response time under 4 hours</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_17c5bed0-a4c0-4c10-a567-0dc5d17ff94d/artifacts/8iq9t1qr_give_me_images_to_put_in_staymii.com_website.jpeg"
                alt="StayMii Support Team"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Stop Worrying About E-Invoice.<br/>
            Start Automating Today.
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join 500+ Malaysian hotels who've already solved e-Invoice compliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6"
              onClick={() => navigate('/einvoice/portal/auth')}
            >
              Start Your Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
              onClick={() => navigate('/contact')}
            >
              <Phone className="mr-2 h-5 w-5" />
              Talk to Sales
            </Button>
          </div>
          <p className="mt-6 text-sm text-blue-200">
            No credit card required • Setup in under 5 minutes • Free migration support
          </p>
        </div>
      </section>

      {/* Shared Footer */}
      <LandingFooter />
    </div>
  );
};

export default EInvoiceProductPage;
